import { useEffect } from 'react';

const CDN='https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
const TARGET_ID='ofisgorev-mobile-barcode-reader-v2';

const sleep=ms=>new Promise(resolve=>window.setTimeout(resolve,ms));

function loadScanner(){
  return new Promise((resolve,reject)=>{
    if(window.Html5Qrcode)return resolve(window.Html5Qrcode);
    const old=document.querySelector('script[data-ofisgorev-html5-qrcode="1"]');
    if(old){
      old.addEventListener('load',()=>window.Html5Qrcode?resolve(window.Html5Qrcode):reject(new Error('Barkod motoru yüklenemedi')),{once:true});
      old.addEventListener('error',()=>reject(new Error('Barkod motoru yüklenemedi')),{once:true});
      return;
    }
    const script=document.createElement('script');
    script.src=CDN;
    script.async=true;
    script.dataset.ofisgorevHtml5Qrcode='1';
    script.onload=()=>window.Html5Qrcode?resolve(window.Html5Qrcode):reject(new Error('Barkod motoru yüklenemedi'));
    script.onerror=()=>reject(new Error('Barkod motoru yüklenemedi'));
    document.head.appendChild(script);
  });
}

function supportedOneDFormats(){
  const F=window.Html5QrcodeSupportedFormats||{};
  return [F.EAN_13,F.EAN_8,F.CODE_128,F.CODE_39,F.CODE_93,F.UPC_A,F.UPC_E,F.ITF].filter(v=>v!==undefined&&v!==null);
}

function setReactInput(input,value){
  if(!input)return;
  const setter=Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype,'value')?.set;
  if(setter)setter.call(input,value);else input.value=value;
  input.dispatchEvent(new Event('input',{bubbles:true}));
  input.dispatchEvent(new Event('change',{bubbles:true}));
}

function makeOverlay(onClose){
  const root=document.createElement('div');
  root.dataset.ofisgorevMobileScanner='1';
  root.style.cssText='position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,.78);display:flex;align-items:center;justify-content:center;padding:12px;';
  root.innerHTML=`<div style="width:100%;max-width:620px;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 24px 80px rgba(0,0,0,.35)">
    <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:16px;border-bottom:1px solid #e5e7eb">
      <div><div style="font-weight:800;font-size:18px;color:#111827">Telefonla Barkod Oku</div><div style="font-size:13px;color:#6b7280;margin-top:4px">Arka kamera önceliklidir. EAN-13, EAN-8, Code128, Code39, Code93, UPC ve ITF barkodları okunur.</div></div>
      <button type="button" data-scanner-close="1" style="width:38px;height:38px;border:1px solid #d1d5db;border-radius:10px;background:#fff;font-size:24px;line-height:1">×</button>
    </div>
    <div style="padding:12px 12px 16px">
      <div id="${TARGET_ID}" style="width:100%;min-height:300px;background:#000;border-radius:14px;overflow:hidden"></div>
      <div data-scanner-status="1" style="font-size:12px;color:#6b7280;margin-top:10px">Arka kamera hazırlanıyor…</div>
    </div>
  </div>`;
  root.querySelector('[data-scanner-close="1"]')?.addEventListener('click',onClose);
  document.body.appendChild(root);
  return root;
}

async function optimizeVideo(root){
  const video=root?.querySelector('video');
  if(!video)return false;
  try{
    video.setAttribute('playsinline','');
    video.setAttribute('webkit-playsinline','');
    video.muted=true;
    video.style.objectFit='cover';
    if(video.paused)await video.play().catch(()=>{});
  }catch(e){}
  await sleep(220);
  const stream=video.srcObject;
  const track=stream?.getVideoTracks?.()[0];
  if(track){
    try{
      const caps=track.getCapabilities?.()||{};
      const advanced=[];
      if(Array.isArray(caps.focusMode)&&caps.focusMode.includes('continuous'))advanced.push({focusMode:'continuous'});
      if(caps.zoom&&Number.isFinite(caps.zoom.max)){
        const min=Number(caps.zoom.min||1),max=Number(caps.zoom.max||1);
        const zoom=Math.min(max,Math.max(min,1.25));
        if(zoom>min)advanced.push({zoom});
      }
      if(advanced.length)await track.applyConstraints({advanced});
    }catch(e){}
  }
  return Number(video.videoWidth||0)>0&&Number(video.videoHeight||0)>0&&video.readyState>=2;
}

function cameraScore(camera,index,total){
  const label=String(camera?.label||'').toLocaleLowerCase();
  let score=0;
  if(/back|rear|environment|arka|traseira|trasera|arrière|posteriore|hinten|achter|задн|الخلف|خلفي|خلفية/i.test(label))score+=100;
  if(/front|user|selfie|ön kamera|ön|frontal|avant|anteriore|vorne|voor|передн|الأمام|امامي|أمامي|أمامية/i.test(label))score-=150;
  score+=total>1?(index/(total-1))*5:0;
  return score;
}

export default function MobileBarcodeScannerBridge(){
  useEffect(()=>{
    let scanner=null;
    let overlay=null;
    let closing=false;

    const cleanup=async()=>{
      if(closing)return;
      closing=true;
      try{if(scanner?.isScanning)await scanner.stop()}catch(e){}
      try{scanner?.clear?.()}catch(e){}
      scanner=null;
      overlay?.remove();
      overlay=null;
      closing=false;
    };

    const start=async(sourceButton)=>{
      await cleanup();
      overlay=makeOverlay(()=>cleanup());
      const status=overlay.querySelector('[data-scanner-status="1"]');
      try{
        const Html5Qrcode=await loadScanner();
        const formats=supportedOneDFormats();
        let locked=false;
        const success=async text=>{
          if(locked)return;
          const value=String(text||'').replace(/\s+/g,'').trim();
          if(!value)return;
          locked=true;
          const input=sourceButton?.parentElement?.querySelector('input')||document.querySelector('input[placeholder*="Barkod / SKU"],input[placeholder*="Ürün barkodu"]');
          await cleanup();
          setReactInput(input,value);
          window.setTimeout(()=>{
            const qty=[...document.querySelectorAll('input[type="number"]')].find(x=>String(x.placeholder||'').includes('Adedi')||String(x.placeholder||'').includes('barkod okutun'));
            qty?.focus();qty?.select?.();
          },180);
        };
        const config={
          fps:18,
          qrbox:(w,h)=>({width:Math.max(260,Math.min(w-24,560)),height:Math.max(120,Math.min(Math.round(h*.33),190))}),
          disableFlip:false,
          aspectRatio:1.777778,
        };
        let started=false,lastError=null;

        // Priority 1 on both iPhone and Android: force the environment camera.
        // Device language is irrelevant because this uses media constraints, not camera labels.
        try{
          scanner=new Html5Qrcode(TARGET_ID,{formatsToSupport:formats,useBarCodeDetectorIfSupported:true,verbose:false});
          status.textContent='Arka kamera doğrudan açılıyor…';
          await scanner.start({facingMode:{exact:'environment'}},config,success,()=>{});
          await sleep(700);
          if(!(await optimizeVideo(overlay)))throw new Error('Arka kamera görüntüsü alınamadı');
          started=true;
          status.textContent='Arka kamera hazır · barkodu beyaz çerçeve içinde yatay ve net tutun.';
        }catch(e){
          lastError=e;
          try{if(scanner?.isScanning)await scanner.stop()}catch(_){}
          try{scanner?.clear?.()}catch(_){}
          scanner=null;
        }

        // Priority 2: enumerate cameras and score rear-camera labels, including Arabic labels.
        if(!started){
          const cameras=await Html5Qrcode.getCameras();
          if(!cameras?.length)throw new Error('Kullanılabilir kamera bulunamadı');
          const ordered=[...cameras]
            .map((camera,index)=>({camera,score:cameraScore(camera,index,cameras.length)}))
            .sort((a,b)=>b.score-a.score)
            .map(x=>x.camera);

          for(let i=0;i<Math.min(ordered.length,5);i++){
            try{
              if(scanner){try{if(scanner.isScanning)await scanner.stop()}catch(e){}try{scanner.clear()}catch(e){}}
              scanner=new Html5Qrcode(TARGET_ID,{formatsToSupport:formats,useBarCodeDetectorIfSupported:true,verbose:false});
              status.textContent=`Arka kamera aranıyor${ordered[i]?.label?` · ${ordered[i].label}`:''}…`;
              await scanner.start(ordered[i].id,config,success,()=>{});
              await sleep(650);
              if(await optimizeVideo(overlay)){
                started=true;
                status.textContent='Kamera hazır · barkodu beyaz çerçeve içinde yatay ve net tutun.';
                break;
              }
              lastError=new Error('Kamera görüntüsü alınamadı');
            }catch(e){lastError=e;}
          }
        }

        // Priority 3: soft environment preference for browsers that reject exact constraints.
        if(!started){
          try{
            if(scanner){try{if(scanner.isScanning)await scanner.stop()}catch(e){}try{scanner.clear()}catch(e){}}
            scanner=new Html5Qrcode(TARGET_ID,{formatsToSupport:formats,useBarCodeDetectorIfSupported:true,verbose:false});
            status.textContent='Arka kamera uyumluluk modunda açılıyor…';
            await scanner.start({facingMode:'environment'},{...config,aspectRatio:1.333333},success,()=>{});
            await sleep(800);
            if(!(await optimizeVideo(overlay)))throw new Error('Kamera görüntüsü alınamadı');
            started=true;
            status.textContent='Kamera hazır · barkodu beyaz çerçeve içinde yatay ve net tutun.';
          }catch(e){lastError=e;}
        }
        if(!started)throw lastError||new Error('Kamera başlatılamadı');
      }catch(e){
        console.error('OfisGorev mobile scanner:',e);
        if(status){status.style.color='#b91c1c';status.textContent=`Kamera açılamadı: ${String(e?.message||e||'Bilinmeyen hata')}. Tarayıcı kamera iznini kontrol edin.`;}
      }
    };

    const onClick=event=>{
      if(!window.location.pathname.startsWith('/depo/stok-hareketleri'))return;
      const button=event.target?.closest?.('button');
      if(!button||!String(button.textContent||'').includes('Telefonla Oku'))return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      start(button);
    };
    document.addEventListener('click',onClick,true);
    return()=>{document.removeEventListener('click',onClick,true);cleanup()};
  },[]);
  return null;
}
